import { useState, useEffect } from 'react';
import { Star, ThumbsUp, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewSection = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    rating: 5,
    review_text: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [productId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${productId}`);
      setReviews(response.data.reviews);
      setAverageRating(response.data.average_rating);
      setTotalReviews(response.data.total_reviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.email || !formData.review_text) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: productId,
        ...formData
      });
      
      toast.success('Review submitted successfully!');
      setShowForm(false);
      setFormData({
        customer_name: '',
        email: '',
        rating: 5,
        review_text: ''
      });
      fetchReviews();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ rating, size = 'sm' }) => {
    const sizeClasses = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6'
    };

    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses[size]} ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-slate-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="mt-12" data-testid="review-section">
      <div className="border-t border-slate-200 pt-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Customer Reviews
            </h2>
            {totalReviews > 0 && (
              <div className="flex items-center gap-4">
                <StarRating rating={averageRating} size="md" />
                <span className="text-lg font-semibold text-slate-900">{averageRating.toFixed(1)}</span>
                <span className="text-slate-600">({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})</span>
              </div>
            )}
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-sky-500 hover:bg-sky-600 text-white rounded-full"
            data-testid="write-review-btn"
          >
            Write a Review
          </Button>
        </div>

        {showForm && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-8" data-testid="review-form">
            <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Share Your Experience
            </h3>
            <form onSubmit={handleSubmitReview} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Your Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="mt-2"
                    disabled={submitting}
                    data-testid="review-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-2"
                    disabled={submitting}
                    data-testid="review-email-input"
                  />
                </div>
              </div>

              <div>
                <Label>Rating</Label>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      disabled={submitting}
                      data-testid={`star-${star}`}
                    >
                      <Star
                        className={`h-8 w-8 cursor-pointer transition-colors ${
                          star <= formData.rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-slate-300 hover:text-yellow-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="review">Your Review</Label>
                <Textarea
                  id="review"
                  value={formData.review_text}
                  onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                  rows={4}
                  className="mt-2"
                  placeholder="Share your experience with this formula..."
                  disabled={submitting}
                  data-testid="review-text-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-sky-500 hover:bg-sky-600 text-white rounded-full"
                  data-testid="submit-review-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Review'}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowForm(false)}
                  variant="outline"
                  className="rounded-full"
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {totalReviews === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-2xl" data-testid="no-reviews">
            <p className="text-slate-600 text-lg mb-4">No reviews yet. Be the first to review this formula!</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-sky-500 hover:bg-sky-600 text-white rounded-full"
            >
              Write First Review
            </Button>
          </div>
        ) : (
          <div className="space-y-6" data-testid="reviews-list">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="bg-white border border-slate-200 rounded-2xl p-6"
                data-testid={`review-${review.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-bold text-slate-900">{review.customer_name}</h4>
                      {review.verified_purchase && (
                        <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          <CheckCircle2 className="h-3 w-3" />
                          Verified Purchase
                        </span>
                      )}
                    </div>
                    <StarRating rating={review.rating} size="sm" />
                  </div>
                  <span className="text-sm text-slate-500">{formatDate(review.created_at)}</span>
                </div>
                <p className="text-slate-700 leading-relaxed">{review.review_text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewSection;
